import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import MoleculeImage from './MoleculeImage';
import API_BASE from '../config';
import { authHeaders, clearEditPassword } from '../services/auth';
import './Triage.css';

const Triage = () => {
  const [reactions, setReactions] = useState([]);
  const [index, setIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [pendingRoles, setPendingRoles] = useState({});
  const [editedCount, setEditedCount] = useState(0);
  const [done, setDone] = useState(false);
  const [gotoInput, setGotoInput] = useState('');
  const [gotoError, setGotoError] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const resp = await fetch(`${API_BASE}/reactions?per_page=500`);
        const data = await resp.json();
        setReactions(data.reactions || []);
      } catch (err) {
        setFetchError('Failed to load reactions: ' + err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const reaction = reactions[index];

  // Reset pending roles whenever the reaction changes
  useEffect(() => {
    if (reaction) {
      const initial = {};
      reaction.molecules.forEach(m => { initial[m.id] = m.role; });
      setPendingRoles(initial);
      setSaveError(null);
    }
  }, [index]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRoleChange = (molId, newRole) => {
    setPendingRoles(prev => ({ ...prev, [molId]: newRole }));
  };

  const advance = useCallback((currentIndex, total) => {
    if (currentIndex >= total - 1) {
      setDone(true);
    } else {
      setIndex(currentIndex + 1);
    }
  }, []);

  const saveAndNext = useCallback(async () => {
    if (!reaction || saving) return;
    setSaving(true);
    setSaveError(null);

    const changed = reaction.molecules.filter(m => pendingRoles[m.id] !== m.role);

    try {
      for (const mol of changed) {
        const resp = await fetch(`${API_BASE}/molecules/${mol.id}/role`, {
          method: 'PATCH',
          headers: authHeaders(),
          body: JSON.stringify({ role: pendingRoles[mol.id] }),
        });
        if (resp.status === 401) { clearEditPassword(); throw new Error('Wrong password'); }
        if (!resp.ok) {
          const data = await resp.json();
          throw new Error(data.error || 'Save failed');
        }
      }

      if (changed.length > 0) {
        setEditedCount(c => c + 1);
        // Update local copy so going Back shows saved state
        setReactions(prev => prev.map((r, i) => {
          if (i !== index) return r;
          return {
            ...r,
            molecules: r.molecules.map(m => ({ ...m, role: pendingRoles[m.id] ?? m.role })),
          };
        }));
      }

      advance(index, reactions.length);
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  }, [reaction, saving, pendingRoles, index, reactions.length, advance]);

  const skip = useCallback(() => {
    if (saving) return;
    advance(index, reactions.length);
  }, [saving, index, reactions.length, advance]);

  const handleGoto = useCallback(() => {
    const target = parseInt(gotoInput, 10);
    if (isNaN(target)) return;
    const idx = reactions.findIndex(r => r.record_number === target);
    if (idx === -1) {
      setGotoError(true);
      return;
    }
    setGotoError(false);
    setGotoInput('');
    setDone(false);
    setIndex(idx);
  }, [gotoInput, reactions]);

  const goBack = useCallback(() => {
    if (index > 0) {
      setDone(false);
      setIndex(i => i - 1);
    }
  }, [index]);

  // Keyboard shortcuts — don't fire when a <select> is focused
  useEffect(() => {
    const handleKey = (e) => {
      if (e.target.tagName === 'SELECT' || e.target.tagName === 'INPUT') return;
      if (e.key === 'Enter' || e.key === 'ArrowRight') { e.preventDefault(); saveAndNext(); }
      if (e.key === 's' || e.key === 'S') { e.preventDefault(); skip(); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); goBack(); }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [saveAndNext, skip, goBack]);

  if (loading) return <div className="triage-status">Loading reactions...</div>;
  if (fetchError) return <div className="triage-status triage-status-error">{fetchError}</div>;

  if (done) {
    return (
      <div className="triage-done">
        <div className="triage-done-icon">✓</div>
        <h2>All done!</h2>
        <p>You reviewed all {reactions.length} reactions and edited roles for {editedCount}.</p>
        <div className="triage-done-actions">
          <button className="triage-btn-restart" onClick={() => { setIndex(0); setDone(false); setEditedCount(0); }}>
            Start Over
          </button>
          <Link to="/" className="triage-btn-home">Back to Reactions</Link>
        </div>
      </div>
    );
  }

  if (!reaction) return null;

  const isLast = index >= reactions.length - 1;
  const progress = ((index + 1) / reactions.length) * 100;

  return (
    <div className="triage-container">
      <div className="triage-header">
        <div className="triage-header-left">
          <Link to="/" className="triage-exit">← Exit</Link>
          <span className="triage-title">Triage Mode</span>
        </div>
        <div className="triage-progress-wrap">
          <div className="triage-progress-bar">
            <div className="triage-progress-fill" style={{ width: `${progress}%` }} />
          </div>
          <span className="triage-counter">{index + 1} / {reactions.length}</span>
          {editedCount > 0 && <span className="triage-edited-badge">{editedCount} edited</span>}
          <div className="triage-goto">
            <input
              className={`triage-goto-input${gotoError ? ' triage-goto-error' : ''}`}
              type="number"
              placeholder="Go to #"
              value={gotoInput}
              onChange={e => { setGotoInput(e.target.value); setGotoError(false); }}
              onKeyDown={e => e.key === 'Enter' && handleGoto()}
            />
            <button className="triage-goto-btn" onClick={handleGoto}>Go</button>
          </div>
        </div>
        <div className="triage-reaction-meta">
          <strong>#{reaction.record_number}</strong>
          <span className="triage-type-badge">{reaction.reaction_type}</span>
          <Link to={`/reaction/${reaction.record_number}`} className="triage-detail-link" target="_blank">
            Open ↗
          </Link>
        </div>
      </div>

      <div className="triage-body">
        <div className="triage-molecules">
          {reaction.molecules.map(mol => {
            const role = pendingRoles[mol.id] ?? mol.role;
            return (
              <div key={mol.id} className={`triage-mol-card triage-role-${role}`}>
                <MoleculeImage smiles={mol.smiles} width={200} height={200} />
                <select
                  className={`triage-role-select triage-select-${role}`}
                  value={role}
                  onChange={e => handleRoleChange(mol.id, e.target.value)}
                >
                  <option value="reactant">reactant</option>
                  <option value="reagent">reagent</option>
                  <option value="product">product</option>
                </select>
                <div className="triage-smiles">{mol.smiles}</div>
              </div>
            );
          })}
        </div>

        {reaction.reagent_table && (
          <div className="triage-conditions">
            <span className="triage-conditions-label">Conditions:</span>
            {reaction.reagent_table}
          </div>
        )}
      </div>

      {saveError && <div className="triage-save-error">{saveError}</div>}

      <div className="triage-footer">
        <button className="triage-btn triage-btn-back" onClick={goBack} disabled={index === 0 || saving}>
          ← Back
        </button>
        <button className="triage-btn triage-btn-skip" onClick={skip} disabled={saving}>
          Skip →
        </button>
        <button className="triage-btn triage-btn-save" onClick={saveAndNext} disabled={saving}>
          {saving ? 'Saving…' : isLast ? 'Save & Finish ✓' : 'Save & Next →'}
        </button>
        <div className="triage-shortcuts">
          <kbd>Enter</kbd> or <kbd>→</kbd> Save &amp; Next &nbsp;·&nbsp;
          <kbd>S</kbd> Skip &nbsp;·&nbsp;
          <kbd>←</kbd> Back
        </div>
      </div>
    </div>
  );
};

export default Triage;
